import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Space, Input, message, 
  Tag, Card, Typography, Tooltip, Select, Row, Col,
  Badge
} from 'antd';
import {
  DownloadOutlined, ReloadOutlined, SearchOutlined,
  FileExcelOutlined, UserOutlined, CalendarOutlined
} from '@ant-design/icons';
import { adminApi } from '../../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const ProcessingHistory = () => {
  const [histories, setHistories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    // 登录后自动查询
    fetchHistories();
  }, [pagination.current, pagination.pageSize, selectedUserId]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      message.error('请先登录管理员账号');
      return null;
    }
    return { 'Authorization': `Bearer ${token}` };
  };

  const fetchHistories = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const params = {
        page: pagination.current,
        size: pagination.pageSize,
      };
      
      if (selectedUserId) {
        params.user_id = selectedUserId;
      }

      const response = await adminApi.getProcessingHistory(params);

      if (response.success) {
        setHistories(response.data.histories || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0
        }));
      } else {
        message.error(response.message || '获取处理历史失败');
      }
    } catch (error) {
      console.error('获取处理历史失败:', error);
      if (error.response?.status === 401) {
        message.error('管理员会话已过期，请重新登录');
      } else {
        message.error('获取处理历史失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (sessionId, filename) => {
    try {
      message.loading({ content: '正在下载文件...', key: 'download' });

      const response = await adminApi.downloadUserData(sessionId);
      
      // 从响应中获取blob（响应拦截器返回的是完整response对象）
      const blob = response.data || response;
      
      // 检查blob是否有效
      if (!blob) {
        throw new Error('响应数据为空');
      }
      
      // 如果返回的是错误信息（JSON格式），尝试解析
      if (blob instanceof Blob && blob.type === 'application/json') {
        const text = await blob.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.detail || errorData.message || '下载失败');
        } catch (parseError) {
          throw new Error('服务器返回错误信息');
        }
      }
      
      // 确保是Blob对象
      if (!(blob instanceof Blob)) {
        throw new Error('无效的响应数据格式');
      }
      
      // 从响应头获取文件名（如果有）
      let downloadFilename = filename || `processed_data_${sessionId}.xlsx`;
      if (response.headers) {
        const disposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
        if (disposition) {
          const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            downloadFilename = decodeURIComponent(filenameMatch[1].replace(/['"]/g, ''));
          }
        }
      }
      
      // 创建下载链接
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadFilename;
      document.body.appendChild(a);
      a.click();
      
      // 清理
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);

      message.success({ content: '文件下载成功', key: 'download' });
    } catch (error) {
      console.error('下载文件失败:', error);
      let errorMessage = '下载文件失败';
      
      try {
        if (error.response) {
          // axios错误响应
          if (error.response.data instanceof Blob && error.response.data.type === 'application/json') {
            const text = await error.response.data.text();
            const errorData = JSON.parse(text);
            errorMessage = errorData.detail || errorData.message || '下载失败';
          } else if (error.response.data && typeof error.response.data === 'object') {
            errorMessage = error.response.data.detail || error.response.data.message || '下载失败';
          } else {
            errorMessage = `下载失败: ${error.response.status} ${error.response.statusText}`;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
      } catch (parseError) {
        // 如果解析错误信息失败，使用默认消息
        errorMessage = error.message || '下载文件失败';
      }
      
      message.error({ content: errorMessage, key: 'download' });
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    // 可以根据文件名搜索
    if (value) {
      const filtered = histories.filter(h => 
        h.original_filename?.toLowerCase().includes(value.toLowerCase()) ||
        h.user_info?.username?.toLowerCase().includes(value.toLowerCase())
      );
      setHistories(filtered);
    } else {
      fetchHistories();
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户信息',
      key: 'user_info',
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Space>
            <UserOutlined />
            <span>{record.user_info?.username || `用户${record.user_id}`}</span>
          </Space>
          {record.user_info?.email && (
            <span style={{ fontSize: '12px', color: '#999' }}>
              {record.user_info.email}
            </span>
          )}
        </Space>
      )
    },
    {
      title: '原始文件名',
      dataIndex: 'original_filename',
      key: 'original_filename',
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <Space>
            <FileExcelOutlined />
            <span>{text || '-'}</span>
          </Space>
        </Tooltip>
      )
    },
    {
      title: '处理统计',
      key: 'statistics',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <div>
            <Tag color="blue">总行数: {record.total_rows || 0}</Tag>
            <Tag color="green">成功: {record.processed_rows || 0}</Tag>
          </div>
          {record.failed_rows > 0 && (
            <Tag color="red">失败: {record.failed_rows}</Tag>
          )}
          <Tag color={record.success_rate === 100 ? 'success' : 'warning'}>
            成功率: {record.success_rate?.toFixed(1) || 0}%
          </Tag>
        </Space>
      )
    },
    {
      title: '文件状态',
      key: 'file_status',
      width: 120,
      render: (_, record) => (
        <Badge 
          status={record.has_excel_file ? 'success' : 'error'} 
          text={record.has_excel_file ? '有文件' : '无文件'} 
        />
      )
    },
    {
      title: '处理时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text) => (
        <Space>
          <CalendarOutlined />
          <span>{text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-'}</span>
        </Space>
      ),
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          {record.has_excel_file ? (
            <Tooltip title="下载Excel文件">
              <Button 
                type="primary" 
                icon={<DownloadOutlined />} 
                size="small"
                onClick={() => handleDownload(
                  record.session_id, 
                  record.original_filename || `processed_${record.session_id}.xlsx`
                )}
              >
                下载
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title="该记录没有可下载的文件">
              <Button 
                disabled 
                icon={<DownloadOutlined />} 
                size="small"
              >
                无文件
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4} style={{ margin: 0 }}>
                <FileExcelOutlined /> 处理历史管理
              </Title>
            </Col>
            <Col>
              <Space>
                <Search
                  placeholder="搜索文件名或用户名"
                  allowClear
                  enterButton={<SearchOutlined />}
                  size="middle"
                  onSearch={handleSearch}
                  onChange={(e) => {
                    if (!e.target.value) {
                      fetchHistories();
                    }
                  }}
                  style={{ width: 300 }}
                />
                <Select
                  placeholder="筛选用户"
                  allowClear
                  style={{ width: 200 }}
                  onChange={(value) => {
                    setSelectedUserId(value);
                    setPagination(prev => ({ ...prev, current: 1 }));
                  }}
                >
                  {/* 这里可以添加用户列表，暂时留空，后续可以优化 */}
                </Select>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={fetchHistories}
                  loading={loading}
                >
                  刷新
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        <Table
          columns={columns}
          dataSource={histories}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({
                ...prev,
                current: page,
                pageSize
              }));
            }
          }}
        />
      </Card>
    </div>
  );
};

export default ProcessingHistory;

